import React, { useState, useEffect } from 'react';
import { dispatch } from '@wordpress/data';
import { Loader } from 'react-feather';
import ReactDOM from 'react-dom';
import { IconWarnSVG, IconCloseSVG, IconRadioCheckedSVG, IconRadioSVG } from '../../icon';
import { __ } from '@wordpress/i18n';
import { getConfig, saveDemoImportState, installPlugins, getContent, importImage, importContent, importStyle, uninstallContent } from '../../helper/axios';
import UpgradePopup from '../banner-pro';

const importer = (demo, action, origin) => {
    const container = document.getElementById('jkit-dashboard-import');
    if (container) {
        ReactDOM.render(
            <Importer demo={demo} action={action} origin={origin} />,
            container
        );
    }
};

export const Importer = ({ demo, action: install, origin='dashboard' }) => {
    const { slug, title } = demo;
    const container = document.getElementById('jkit-dashboard-import');
    const options = [
        { title: 'Full Import', subtitle: 'We will import all demo contents, the global styles and install all required plugins for this demo.', value: 'full' },
        { title: 'Demo Content Only', subtitle: 'We will import all demo contents and install all required plugins for this demo.', value: 'content' },
        { title: 'Global Style Only', subtitle: 'We will import the global styles only', value: 'style' },
    ];
    const [importState, setImportState] = useState({
        full: [
            { title: 'Preparing data', value: 'preparing-data', state: false },
            { title: 'Install required plugins', value: 'install-plugin', state: false },
            { title: 'Importing images', value: 'import-image', state: false },
            { title: 'Importing contents', value: 'import-content', state: false },
            { title: 'Importing global style', value: 'import-style', state: false },
        ],
        content: [
            { title: 'Preparing data', value: 'preparing-data', state: false },
            { title: 'Install required plugins', value: 'install-plugin', state: false },
            { title: 'Importing images', value: 'import-image', state: false },
            { title: 'Importing contents', value: 'import-content', state: false },
        ],
        style: [
            { title: 'Preparing data', value: 'preparing-data', state: false },
            { title: 'Importing global style', value: 'import-style', state: false },
        ],
        uninstall: [
            { title: 'Preparing data', value: 'preparing-data', state: false },
            { title: 'Uninstalling images', value: 'uninstall-image', state: false },
            { title: 'Uninstalling contents', value: 'uninstall-content', state: false },
            { title: 'Uninstalling global style', value: 'uninstall-style', state: false },
        ],
    });
    const [lockImporter, setLockImporter] = useState(false);
    const [selectedValue, setSelectedValue] = useState(options[0].value);
    const [importingState, setImportingState] = useState(false);
    const [demoConfig, setDemoConfig] = useState(false);
    const [action, setAction] = useState(install);
    const [stepStatus, setStepStatus] = useState('');
    const [showBanner, setShowBanner] = useState(true)
    const { proActive } = window['JkitDashboardOption'];

    const handleRadioChange = (event) => {
        setSelectedValue(event.target.value);
    };

    const doneState = (array, value, newState) => {
        return array.map(item => {
            if (item.value === value) {
                return { ...item, state: newState };
            }
            return item;
        });
    };

    const [importExitConfirm, importExitConfirmation] = useState(false);

    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (importExitConfirm) {
                e.preventDefault();
                e.returnValue = '';
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [importExitConfirm]);

    useEffect(() => {
        if (action === 'uninstall') {
            setSelectedValue(action);
        }
    }, [action]);

    useEffect(() => {
        window.essentialpause = false;
        window.essentialfail = false;
        //debug ^^
    }, []);

    /**
     * Reinstall
     */
    const reinstall = () => {
        setSelectedValue(options[0].value);
        setLockImporter(false);
        setImportingState();
        setAction('import');
        setStepStatus('');
    };

    /**
     * Install list of plugins
     */
    const installingPlugins = async (plugins) => {
        var flag;
        for (let [index, plugin] of plugins.entries()) {
            await installPlugins(
                plugin
            ).then(data => {
                if (data) {
                    status(data.status, 1);
                }
            }).catch(error => {
                flag = 'failed';
            });
            if ('failed' === flag) break;
        }

        return 'failed' === flag ? { error: true } : plugins.length;
    };

    const importingImage = async (demo, contents) => {
        var flag = '';
        for (const content of contents) {
            await getContent(demo, content.slug).then(async data => {
                if (data && undefined === data.error) {
                    const dataParse = JSON.parse(data);
                    dataParse.content = await convertImages(dataParse.content);

                    if (dataParse.thumbnail) {
                        dataParse.thumbnail = await importThumbnail(slug, dataParse.thumbnail);
                    }

                    if (dataParse.gallery) {
                        dataParse.gallery = await importThumbnails(slug, dataParse.gallery);
                    }

                    if (dataParse.page_settings.profecient_popup_template_st_content_background_background_image) {
                        dataParse.page_settings.profecient_popup_template_st_content_background_background_image = await switchImage(dataParse.page_settings.profecient_popup_template_st_content_background_background_image);
                    }

                    content.content = dataParse;
                }
            }).catch(error => {
                flag = 'failed';
            });

            if ('failed' === flag) break;
        }

        if ('failed' === flag) {
            return { error: true };
        }

        return contents;
    };

    /**
     * Import single thumbnail
     */
    async function importThumbnail(demo, image) {
        const response = await importImage(demo, image);

        if (response && response.count) {
            status(response.status, 1);
            return response.id;
        } else {
            status(response.status, 0);
        }

        return image;
    }

    /**
     * Import multiple image thumbnails
     */
    async function importThumbnails(demo, images) {
        for (const [index, image] of images.entries()) {
            images[index] = await importThumbnail(demo, image);
        }
        return images;
    }

    /**
     * Convert images that found on the content
     */
    const convertImages = async (data) => {
        if (Array.isArray(data)) {
            for (const item of data) {
                if (['section', 'column', 'container'].includes(item.elType)) {
                    if (item.settings && item.settings.background_image) {
                        item.settings.background_image = await switchImage(item.settings.background_image);
                    }

                    if (item.settings && item.settings.background_overlay_image) {
                        item.settings.background_overlay_image = await switchImage(
                            item.settings.background_overlay_image
                        );
                    }

                    if (item.settings && item.settings.background_video_fallback) {
                        item.settings.background_video_fallback = await switchImage(
                            item.settings.background_video_fallback
                        );
                    }

                    if (item.elements.length) {
                        await convertImages(item.elements);
                    }
                }

                if (item.elType && 'widget' === item.elType) {
                    // global import
                    if (item.settings && item.settings._mask_image) {
                        item.settings._mask_image = await switchImage(item.settings._mask_image);
                    }
                    
                    if (item.settings && item.settings._background_image) {
                        item.settings._background_image = await switchImage(item.settings._background_image);
                    }

                    if ('image' === item.widgetType) {
                        if (item.settings && item.settings.image) {
                            item.settings.image = await switchImage(item.settings.image);
                        }

                        if (item.settings && item.settings._background_image) {
                            item.settings._background_image = await switchImage(item.settings._background_image);
                        }
                    }

                    if ('image-carousel' === item.widgetType) {
                        if (item.settings && item.settings.carousel) {
                            item.settings.carousel = await switchImage(item.settings.carousel);
                        }
                    }

                    if ('spacer' === item.widgetType) {
                        if (item.settings && item.settings._background_image) {
                            item.settings._background_image = await switchImage(item.settings._background_image);
                        }
                    }

                    if ('image-gallery' === item.widgetType) {
                        if (item.settings && item.settings.wp_gallery) {
                            item.settings.wp_gallery = await switchImage(item.settings.wp_gallery);
                        }
                    }

                    if ('social-icons' === item.widgetType) {
                        item.settings.social_icon_list.forEach(async (social_icon_item, index) => {
                            if (social_icon_item.social_icon.library === 'svg') {
                                item.settings.social_icon_list[index].social_icon.value = await switchImage(social_icon_item.social_icon.value);
                            }
                        });
                    }

                    if ('jkit_social_share' === item.widgetType) {
                        item.settings.sg_social_list.forEach(async (social_share_item, index) => {
                            if (social_share_item.sg_social_icon.library === 'svg') {
                                item.settings.sg_social_list[index].sg_social_icon.value = await switchImage(social_share_item.sg_social_icon.value);
                            }
                        });
                    }

                    if ('elementskit-icon-box' === item.widgetType) {
                        if (item.settings && item.settings.ekit_icon_box_show_image) {
                            item.settings.ekit_icon_box_show_image = await switchImage(
                                item.settings.ekit_icon_box_show_image
                            );
                        }
                    }

                    if ('elementskit-team' === item.widgetType) {
                        if (item.settings && item.settings.ekit_team_image) {
                            item.settings.ekit_team_image = await switchImage(item.settings.ekit_team_image);
                        }
                    }

                    if ('elementskit-funfact' === item.widgetType) {
                        if (item.settings && item.settings.ekit_funfact_icon_image) {
                            item.settings.ekit_funfact_icon_image = await switchImage(
                                item.settings.ekit_funfact_icon_image
                            );
                        }
                    }

                    if ('elementskit-heading' === item.widgetType) {
                        if (item.settings && item.settings.ekit_heading_seperator_image) {
                            item.settings.ekit_heading_seperator_image = await switchImage(
                                item.settings.ekit_heading_seperator_image
                            );
                        }
                    }

                    if ('elementskit-image-box' === item.widgetType) {
                        if (item.settings && item.settings.ekit_image_box_image) {
                            item.settings.ekit_image_box_image = await switchImage(item.settings.ekit_image_box_image);
                        }
                    }

                    if ('ekit-nav-menu' === item.widgetType) {
                        if (item.settings && item.settings.elementskit_nav_menu_logo) {
                            item.settings.elementskit_nav_menu_logo = await switchImage(
                                item.settings.elementskit_nav_menu_logo
                            );
                        }
                    }

                    if ('jkit_nav_menu' === item.widgetType) {
                        if (item.settings && item.settings.sg_mobile_menu_logo) {
                            item.settings.sg_mobile_menu_logo = await switchImage(item.settings.sg_mobile_menu_logo);
                        }
                    }

                    if ('jkit_fun_fact' === item.widgetType) {
                        if (item.settings && item.settings.sg_icon_image) {
                            item.settings.sg_icon_image = await switchImage(item.settings.sg_icon_image);
                        }
                    }

                    if ('jkit_icon_box' === item.widgetType) {
                        if (item.settings && item.settings.sg_icon_image) {
                            item.settings.sg_icon_image = await switchImage(item.settings.sg_icon_image);
                        }

                        if (item.settings && item.settings.st_container_background_background_image) {
                            item.settings.st_container_background_background_image = await switchImage(item.settings.st_container_background_background_image);
                        }

                        if (item.settings && item.settings.st_background_overlay_color_background_image) {
                            item.settings.st_background_overlay_color_background_image = await switchImage(item.settings.st_background_overlay_color_background_image);
                        }
                    }

                    if ('jkit_image_box' === item.widgetType) {
                        if (item.settings && item.settings.sg_image_choose) {
                            item.settings.sg_image_choose = await switchImage(item.settings.sg_image_choose);
                        }
                    }

                    if ('jkit_heading' === item.widgetType) {
                        if (item.settings && item.settings.sg_separator_image) {
                            item.settings.sg_separator_image = await switchImage(item.settings.sg_separator_image);
                        }
                    }

                    if ('jkit_pie_chart' === item.widgetType) {
                        if (item.settings && item.settings.sg_content_icon_image) {
                            item.settings.sg_content_icon_image = await switchImage(
                                item.settings.sg_content_icon_image
                            );
                        }
                    }

                    if ('jkit_team' === item.widgetType) {
                        if (item.settings && item.settings.sg_member_image) {
                            item.settings.sg_member_image = await switchImage(item.settings.sg_member_image);
                        }
                    }

                    /**
                     * Need more loop to find nested image
                     */
                    if ('elementskit-simple-tab' === item.widgetType) {
                        if (item.settings && item.settings.ekit_tab_items) {
                            await convertImages(item.settings.ekit_tab_items);
                        }
                    }

                    if ('elementskit-testimonial' === item.widgetType) {
                        if (item.settings && item.settings.ekit_testimonial_data) {
                            await convertImages(item.settings.ekit_testimonial_data);
                        }
                    }

                    if ('elementskit-client-logo' === item.widgetType) {
                        if (item.settings && item.settings.ekit_client_logo_repiter) {
                            await convertImages(item.settings.ekit_client_logo_repiter);
                        }
                    }

                    if ('jkit_testimonials' === item.widgetType) {
                        if (item.settings && item.settings.sg_testimonials_list) {
                            await convertImages(item.settings.sg_testimonials_list);
                        }
                    }

                    if ('jkit_client_logo' === item.widgetType) {
                        if (item.settings && item.settings.sg_logo_list) {
                            await convertImages(item.settings.sg_logo_list);
                        }
                    }

                    if ('jkit_feature_list' === item.widgetType) {
                        if (item.settings && item.settings.sg_setting_list) {
                            await convertImages(item.settings.sg_setting_list);
                        }
                    }

                    if ('jkit_gallery' === item.widgetType || 'jkit_portfolio_gallery' === item.widgetType) {
                        if (item.settings && item.settings.sg_gallery_list) {
                            await convertImages(item.settings.sg_gallery_list);
                        }
                    }

                    if ('jkit_tabs' === item.widgetType) {
                        if (item.settings && item.settings.sg_content_list) {
                            await convertImages(item.settings.sg_content_list);
                        }
                    }

                    if ('icon-list' === item.widgetType) {
                        if (item.settings && item.settings.icon_list) {
                            Object.keys(item.settings.icon_list).forEach(async (index) => {
                                const iconList = item.settings.icon_list;
                                if (iconList[index].selected_icon && 'svg' === iconList[index].selected_icon.library && iconList[index].selected_icon.value && iconList[index].selected_icon.value.url && '' !== iconList[index].selected_icon.value.url) {
                                    item.settings.icon_list[index].selected_icon.value = await switchImage(iconList[index].selected_icon.value);
                                }
                            });
                        }
                    }

                    Object.keys(item.settings).forEach(async (setting) => {
                        if (item.settings[setting].library === 'svg') {
                            item.settings[setting].value = await switchImage(item.settings[setting].value);
                        }
                    });
                }

                // elementskit-simple-tab
                if (item.ekit_tab_title_image) {
                    item.ekit_tab_title_image = await switchImage(item.ekit_tab_title_image);
                }

                // elementskit-client-logo
                if (item.ekit_client_logo_image_normal) {
                    item.ekit_client_logo_image_normal = await switchImage(item.ekit_client_logo_image_normal);
                }

                // elementskit-testimonial
                if (item.client_photo) {
                    item.client_photo = await switchImage(item.client_photo);
                }

                if (item.client_logo) {
                    item.client_logo = await switchImage(item.client_logo);
                }

                if (item.client_logo_active) {
                    item.client_logo_active = await switchImage(item.client_logo_active);
                }

                // jkit_tabs
                if (item.sg_content_image) {
                    item.sg_content_image = await switchImage(item.sg_content_image);
                }

                // jkit_gallery & jkit_portfolio_gallery
                if (item.sg_gallery_list_image) {
                    item.sg_gallery_list_image = await switchImage(item.sg_gallery_list_image);
                }

                // jkit_feature_list
                if (item.sg_setting_list_image) {
                    item.sg_setting_list_image = await switchImage(item.sg_setting_list_image);
                }

                // jkit_testimonials
                if (item.sg_testimonials_list_client_avatar) {
                    item.sg_testimonials_list_client_avatar = await switchImage(
                        item.sg_testimonials_list_client_avatar
                    );
                }

                if (item.sg_testimonials_list_background_background_image) {
                    item.sg_testimonials_list_background_background_image = await switchImage(item.sg_testimonials_list_background_background_image);
                }

                // jkit_client_logo
                if (item.sg_logo_list_image) {
                    item.sg_logo_list_image = await switchImage(item.sg_logo_list_image);
                }

                if (item.sg_logo_list_hover_logo) {
                    item.sg_logo_list_hover_logo = await switchImage(item.sg_logo_list_hover_logo);
                }
            }
        }

        return data;
    };

    const status = (status, index) => {
        if (index) {
            window.essentialImportIndex++;
        }
        setStepStatus(status + ' (' + window.essentialImportIndex + ')');
    };

    /**
     * Switch original image source into the imported image
     */
    const switchImage = async (data) => {
        const imageHandler = async args => {
            const image = await importImage(slug, args.url);
            if (undefined === image.error) {
                args = image;
                if (image.count) {
                    status(image.status, 1);
                } else {
                    status(image.status, 0);
                }
            } else {
                status(image.status, 0);
            }
            return args;
        };

        if (Array.isArray(data)) {
            for (const [index, item] of data.entries()) {
                if (item.url && '' !== item.url) {
                    data[index] = await imageHandler(item);
                }
            }
        } else {
            if (data.url && '' !== data.url) {
                data = await imageHandler(data);
            }
        }

        return data;
    };

    const nextState = (state) => {
        window.essentialImportIndex = 0;
        //debug style
        if (window.essentialpause) {
            return 'import-image';
        }
        if (window.essentialfail) {
            return 'import-failed';
        }
        const nextFalseStateItem = state.find(item => !item.state);
        return nextFalseStateItem ? nextFalseStateItem.value : 'import-done';
    };

    const importDemo = (slug, flag) => {
        dispatch('jkit/dashboard').demoImport({
            slug,
            flag
        });
        saveDemoImportState({
            slug,
            state: flag,
        }).then(data => {
            if (!data) {
                setImportingState('import-failed');
            }
        });
    };

    const nextImport = (state) => {
        const newState = doneState(importState[selectedValue], state, true);
        setImportState({
            ...importState,
            [selectedValue]: newState,
        });
        setImportingState(nextState(newState));
    };

    /**
     * Import list of contents
     */
    async function importContents(demo, action, contents) {
        var flag = '';
        for (let [index, content] of contents.entries()) {
            await importContent(demo, action, content).then(data => {
                if (data) {
                    status(data.status, 1);
                }
            }).catch(error => {
                flag = 'failed';
                return false;
            });
            if ('failed' === flag) {
                break;
            }
        }
        if ('failed' === flag) {
            return { error: true };
        }

    }

    /**
     * Import global style
     */
    async function importStyles(demo, action) {
        return await getContent(demo, 'global').then(async data => {
            if (data) {
                let content = JSON.parse(data);

                if (content.page_settings && content.page_settings.body_background_image) {
                    content.page_settings.body_background_image = await switchImage(content.page_settings.body_background_image);
                }

                return await importStyle(title, demo, action, content).catch(error => {
                    return data;
                });
            }
        }).catch(error => {
            return { error: true };
        });
    }

    const getState = (string) => {
        const item = importState[selectedValue].find(item => item.value === string);
        return item ? item.title : null;
    };

    const goToLicensePage = () => {
        if (proActive) {
            window.open('/wp-admin/admin.php?page=jkit&path=demos', 'blank');
        } else {
            window.open('https://jegkit.com/pricing/?utm_source=jeg-elementor-kit&utm_medium=dashboard', '_blank');
        }
    }

    const apiImportDemo = async (state) => {
        setImportingState(state);
        setStepStatus(getState(importingState) ? getState(importingState) : 'Done');
        switch (importingState) {
            case 'preparing-data':
                if (action === 'import') {
                    await getConfig(slug).then(data => {
                        if (data) {
                            setDemoConfig(JSON.parse(data));
                        }
                        if (JSON.parse(data)) {
                            importDemo(slug, true);
                            nextImport(state);
                        } else {
                            setImportingState('import-failed');
                        }
                    });
                }
                if (action === 'uninstall') {
                    importDemo(slug, false);
                    nextImport(state);
                }
                break;
            case 'install-plugin':
                if (demoConfig) {
                    await installingPlugins(demoConfig.plugins).then(message => {
                        if (undefined === message.error) {
                            nextImport(state);
                        } else {
                            setImportingState('import-failed');
                        }
                    });
                } else {
                    setImportingState('import-failed');
                }
                break;
            case 'import-image':
                if (demoConfig) {
                    await importingImage(slug, demoConfig.contents).then(message => {
                        if (undefined === message.error) {
                            nextImport(state);
                        } else {
                            setImportingState('import-failed');
                        }
                    });
                } else {
                    setImportingState('import-failed');
                }
                break;
            case 'import-content':
                if (demoConfig) {
                    await importContents(slug, 'import', demoConfig.contents).then(data => {
                        //need to define error
                        nextImport(state);
                    });
                } else {
                    setImportingState('import-failed');
                }
                break;
            case 'import-style':
                if (demoConfig) {
                    await importStyles(slug, 'import').then(data => {
                        if (undefined === data.error) {
                            nextImport(state);
                        } else {
                            setImportingState('import-failed');
                        }
                    });
                } else {
                    setImportingState('import-failed');
                }
                break;
            case 'uninstall-image':
            case 'uninstall-content':
            case 'uninstall-style':
                await uninstallContent(importingState, slug, action).then(data => {
                    nextImport(state);
                });
                break;
            default:
                break;
        }

        //import here use 'demo' & 'state'
        // setTimeout(()=>{// add delay for debug
        //     if (state) {// the import status : success / fail
        //         const newState = doneState(importState[selectedValue], state, true);
        //         setImportState({
        //             ...importState,
        //             [selectedValue]: newState,
        //         });
        //         setImportingState(nextState(newState));
        //     } else {
        //         setImportingState('import-failed');
        //     }
        // }, 1000);

    };

    useEffect(() => {
        if (importingState) {
            switch (importingState) {
                case 'preparing-data':
                    setLockImporter(true);
                    importExitConfirmation(true);
                    apiImportDemo(importingState);
                    break;
                case 'import-done':
                    setStepStatus('Completed');
                    setTimeout(() => {
                        setLockImporter(false);
                        importExitConfirmation(false);
                    }, 1000);
                    break;
                case 'import-failed':
                    setStepStatus('Error');
                    setTimeout(() => {
                        setLockImporter(false);
                        importExitConfirmation(false);
                    }, 1000);
                    break;
                default:
                    apiImportDemo(importingState);
                    break;
            }
        }
    }, [
        importingState,
    ]);

    const props = {
        lockImporter,
        container,
        options,
        selectedValue,
        handleRadioChange,
        setLockImporter,
        importingState,
        setImportingState,
        demo,
        importState: importState[selectedValue],
        action,
        setAction,
        reinstall,
        stepStatus,
        origin
    };
    return <>
        <div className="essential-import-importer" onClick={() => {
            if (importingState === 'import-done') {
                window.location.reload();
            } else {
                !lockImporter && ReactDOM.unmountComponentAtNode(container);
            }
        }} />
        <ImporterModal {...props}
            ImporterHeader={({ children }) => {
                return <div className="essential-importer-header" style={((importingState === 'import-done' || importingState === 'import-failed') && !lockImporter) || (action === 'uninstall' && !lockImporter) ? { display: 'none' } : {}}>
                    {children}
                </div>;
            }}
            ImporterBody={({ children }) => {
                return <div className="essential-importer-body">
                    {((importingState === 'import-done' || importingState === 'import-failed') && !lockImporter) || (action === 'uninstall' && !lockImporter) ? <>
                        {!importingState && action !== 'uninstall' ? <div className="essential-close-importer" onClick={() => {
                            !lockImporter && ReactDOM.unmountComponentAtNode(container);
                        }}>
                            <IconCloseSVG />
                        </div> : ''}
                        <div className="essential-finish-body">
                            {importingState === 'import-done' ? <svg xmlns="http://www.w3.org/2000/svg" width="120" height="120" viewBox="0 0 120 120" fill="none">
                                <path d="M110 55.4286V60.0286C109.994 70.8107 106.503 81.302 100.047 89.9377C93.5908 98.5735 84.5164 104.891 74.1768 107.948C63.8372 111.005 52.7863 110.638 42.6724 106.902C32.5584 103.165 23.9233 96.2591 18.0548 87.214C12.1863 78.1688 9.39896 67.4689 10.1084 56.7102C10.8178 45.9514 14.986 35.7103 21.9914 27.514C28.9968 19.3177 38.4639 13.6055 48.9809 11.2293C59.498 8.85317 70.5013 9.9403 80.35 14.3286M110 20L60 70.05L45 55.05" stroke="#933AFF" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                            </svg> : action === 'uninstall' ?
                                <svg xmlns="http://www.w3.org/2000/svg" width="104" height="104" viewBox="0 0 104 104" fill="none">
                                    <path d="M52 32L52.05 60M52 72H52.05M2 34.6137V69.3863C2 70.6092 2 71.2207 2.13815 71.7962C2.26063 72.3063 2.46266 72.7941 2.7368 73.2414C3.04601 73.746 3.47839 74.1784 4.34315 75.0431L28.9569 99.6569C29.8216 100.522 30.254 100.954 30.7586 101.263C31.2059 101.537 31.6937 101.739 32.2038 101.862C32.7793 102 33.3908 102 34.6137 102H69.3863C70.6092 102 71.2207 102 71.7962 101.862C72.3063 101.739 72.7941 101.537 73.2414 101.263C73.746 100.954 74.1784 100.522 75.0431 99.6569L99.6569 75.0431C100.522 74.1784 100.954 73.746 101.263 73.2414C101.537 72.7941 101.739 72.3063 101.862 71.7962C102 71.2207 102 70.6092 102 69.3863V34.6137C102 33.3908 102 32.7793 101.862 32.2038C101.739 31.6937 101.537 31.2059 101.263 30.7586C100.954 30.254 100.522 29.8216 99.6569 28.9569L75.0431 4.34315C74.1784 3.47839 73.746 3.04601 73.2414 2.7368C72.7941 2.46266 72.3063 2.26063 71.7962 2.13815C71.2207 2 70.6092 2 69.3863 2H34.6137C33.3908 2 32.7793 2 32.2038 2.13815C31.6937 2.26063 31.2059 2.46266 30.7586 2.7368C30.254 3.04601 29.8216 3.47839 28.9569 4.34315L4.34315 28.9569C3.47839 29.8216 3.04601 30.254 2.7368 30.7586C2.46266 31.2059 2.26063 31.6937 2.13815 32.2038C2 32.7793 2 33.3908 2 34.6137Z" stroke="#F22047" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                :
                                <svg xmlns="http://www.w3.org/2000/svg" width="104" height="104" viewBox="0 0 104 104" fill="none">
                                    <path d="M67 37L37 67M37 37L67 67M102 52C102 79.6142 79.6142 102 52 102C24.3858 102 2 79.6142 2 52C2 24.3858 24.3858 2 52 2C79.6142 2 102 24.3858 102 52Z" stroke="#F22047" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>}
                            <div className="title">{
                                importingState === 'import-done' && action === 'uninstall' ?
                                    __('Uninstall Demo Completed', 'jkit') :
                                    importingState === 'import-done' ?
                                        __('Install Demo Completed', 'jkit') : importingState === 'import-failed' && action === 'uninstall' ?
                                            __('Uninstall Demo Failed', 'jkit')
                                            : action === 'uninstall' ?
                                                __('Are you sure uninstall this demo?', 'jkit')
                                                :
                                                __('Install Demo Failed', 'jkit')
                            }</div>
                            <span style={((importingState !== 'import-done' || importingState !== 'import-failed') && action === 'uninstall') ? { padding: '0 20px' } : importingState !== 'import-done' ? { padding: '0 50px' } : {}}>{
                                importingState === 'import-done' ?
                                    <>
                                        {__('The demo “', 'jkit')}
                                        <span>{demo.title}</span>
                                        {__(`” is successfully ${action === 'uninstall' ? 'uninstalled!' : 'imported!'}`, 'jkit')}
                                    </> : importingState === 'import-failed' && action === 'uninstall' ?
                                        <>
                                            {__('Please try to ”', 'jkit')}
                                            <span>{__('Re-Install', 'jkit')}</span>
                                            {__('” or “', 'jkit')}
                                            <span>{__('Uninstall', 'jkit')}</span>
                                            {__('” the demo.', 'jkit')}
                                        </>
                                        : action === 'uninstall' ?
                                            <>
                                                {__('Uninstalling the demo will remove all previously imported content from this demo', 'jkit')}
                                            </>
                                            :
                                            <>
                                                {__('Importing “', 'jkit')}
                                                <span>{demo.title}</span>
                                                {__('” demo failed, please try to reinstall demo.', 'jkit')}
                                            </>
                            }</span>
                        </div>
                    </> : children}
                </div>;
            }}
        />
        {showBanner && !proActive && (importingState === 'import-done') && (importingState !== 'import-failed') && <UpgradePopup closeState={setShowBanner} goToLicensePage={goToLicensePage} />}
    </>;
};

const ImporterModal = ({
    lockImporter,
    container,
    options,
    selectedValue,
    handleRadioChange,
    importingState,
    setImportingState,
    demo,
    importState,
    ImporterHeader,
    ImporterBody,
    action,
    setAction,
    reinstall,
    stepStatus,
    origin
}) => {
    return <>
        <div className="essential-importer-modal">
            <ImporterHeader>
                <h2>{!lockImporter ? __('Important Notice', 'jkit') : action == 'uninstall' ? __('Uninstalling Demo', 'jkit') : __('Importing Demo', 'jkit')}</h2>
                {!importingState && action !== 'uninstall' ? <div className="essential-close-importer" onClick={() => {
                    !lockImporter && ReactDOM.unmountComponentAtNode(container);
                }}>
                    <IconCloseSVG />
                </div> : ''}
            </ImporterHeader>
            <ImporterBody>
                <div className={'essential-importer-notice'}>
                    <IconWarnSVG />
                    <span>
                        {__(`Please do not refresh or close the page while ${action === 'uninstall' ? 'uninstalling' : 'importing'} data is in progress.`, 'jkit')}
                    </span>
                </div>
                <div className="essential-importer-radio-wrapper">
                    <span>
                        {!lockImporter ? __('Choose how you want to import this demo:', 'jkit') :
                            <>
                                {action == 'uninstall' ? __('Uninstalling “', 'jkit') : __('Importing “', 'jkit')}
                                <span>{demo.title}</span>
                                {__('” demo in progress...', 'jkit')}
                            </>
                        }
                    </span>
                    {!lockImporter ? options.map((option) => (
                        <label key={option.value}>
                            <div className="essential-radio-icon">
                                {selectedValue === option.value ? <IconRadioCheckedSVG /> : <IconRadioSVG />}
                            </div>
                            <input
                                type="radio"
                                value={option.value}
                                checked={selectedValue === option.value}
                                onChange={handleRadioChange}
                            />
                            <div className="radio-content">
                                <h3>{option.title}</h3>
                                <p>{option.subtitle}</p>
                            </div>
                        </label>
                    )) :
                        importState.map((option) => {
                            const name = (() => {
                                let name = false;
                                importState.map(val => {
                                    if (!val.state && !name) {
                                        name = val.title;
                                    }
                                });
                                return name;
                            })();
                            return (
                                <label key={option.value}>
                                    <div className="essential-radio-icon">
                                        {option.state ? <IconRadioCheckedSVG /> : <IconRadioSVG />}
                                    </div>
                                    <input
                                        type="radio"
                                        value={option.value}
                                        checked={option.state}
                                    />
                                    <div className="radio-content">
                                        <h3 className={`${!option.state ? 'process ' : ''} ${option.title === name ? 'current' : ''}`}>
                                            {option.title}
                                            {option.title === name ? <div className="loading"><Loader size={20} /></div> : ''}
                                        </h3>
                                    </div>
                                </label>
                            );
                        })
                    }
                </div>
            </ImporterBody>
            <ImporterFooter stepStatus={stepStatus} reinstall={reinstall} setAction={setAction} lockImporter={lockImporter} importingState={importingState} setImportingState={setImportingState} importState={importState} container={container} action={action} origin={origin}>
                {action === 'import' ? <div className="essential-import-button" onClick={() => {
                    setImportingState('preparing-data');
                }}>
                    {__('Start Import', 'jkit')}
                </div> : <>
                    <div className="essential-uninstall-button" onClick={() => {
                        setImportingState('preparing-data');
                    }}>
                        {__('Uninstall', 'jkit')}
                    </div>
                    <div className="essential-cancel-button" onClick={() => {
                        !lockImporter && ReactDOM.unmountComponentAtNode(container);
                    }}>
                        {__('Cancel', 'jkit')}
                    </div>
                </>}
            </ImporterFooter>
        </div>
    </>;
};

const ImporterFooter = ({ children, lockImporter, importingState, importState, container, action, setImportingState, reinstall, stepStatus, origin }) => {
    const stateCount = importState.length;
    const trueCount = importState.filter(item => item.state === true).length;

    return <div className={`essential-importer-footer ${lockImporter ? 'block' : ''}`} style={(importingState === 'import-done' || importingState === 'import-failed' || action === 'uninstall') && !lockImporter ? { border: 'unset' } : {}}>
        {((importingState === 'import-done' || importingState === 'import-failed') && !lockImporter && action !== 'uninstall') || (importingState === 'import-done' && action === 'uninstall' && !lockImporter) ? <>
            <div className="essential-import-button" onClick={() => {
                if (importingState === 'import-done' && 'wizard' !== origin) {
                    window.location.reload();
                } else {
                    !lockImporter && ReactDOM.unmountComponentAtNode(container);
                }
            }}>
                {__('OK', 'jkit')}
            </div>
        </> : (importingState === 'import-done' || importingState === 'import-failed') && !lockImporter ?
            <>
                <div className="essential-import-button" onClick={() => {
                    reinstall();
                }}>
                    {__('Re-Install', 'jkit')}
                </div>
                <div className="essential-uninstall-second-button" onClick={() => {
                    setImportingState('preparing-data');
                }}>
                    {__('Uninstall', 'jkit')}
                </div>
            </>
            : !lockImporter ? children : <>
                <div className="essential-footer-import">
                    <span>{stepStatus}</span>
                    <span>{trueCount}/{stateCount} {__('Completed', 'jkit')}</span>
                </div>
                <div className="essential-footer-import-progress">
                    <span></span>
                    <span style={{ width: `${(trueCount / stateCount) * 100}%` }}></span>
                </div>
            </>}
    </div>;
};

export default importer;
